import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BrandWordmark } from "@/components/BrandWordmark";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithData } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithData(data);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your studio">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
        <div className="text-right -mt-1">
          <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-zinc-500 hover:text-blue-400 transition-colors">Forgot password?</Link>
        </div>
        <button disabled={loading} data-testid="login-submit" className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-2.5 rounded-md font-semibold transition-colors">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-xs text-zinc-600 uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>
      <GoogleSignInButton />
      <p className="text-sm text-zinc-500 mt-6 text-center">
        No account? <Link to="/register" className="text-blue-400 hover:text-blue-300">Create one</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] grid-noise flex items-center justify-center px-5">
      <Seo title={title} description="Sign in or create your Videoileo account to start building a faceless video channel with AI." noindex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <BrandWordmark className="h-14" />
        </Link>
        <div className="border border-white/10 rounded-md bg-[#121212] p-8">
          <h1 className="font-head text-2xl font-bold">{title}</h1>
          <p className="text-sm text-zinc-500 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
      />
    </div>
  );
}
